import { Platform } from "@src/mobile/utils/platform";

export interface AppInfo {
  name: string;
  version: string;
  build: string;
  id: string;
}

export interface AppLaunchUrl {
  url: string;
}

export interface AppUrlOpenEvent {
  url: string;
}

export interface AppStateChange {
  isActive: boolean;
}

export interface AppListenerHandle {
  remove: () => Promise<void>;
}

const NO_OP_LISTENER_HANDLE: AppListenerHandle = {
  remove: async () => {
    // No-op for web platform
  },
};

const WEB_APP_INFO: AppInfo = {
  name: "",
  version: "",
  build: "",
  id: "",
};

/**
 * Returns the installed app's metadata. On web, returns empty strings —
 * callers should treat empty `version` / `build` as "not running natively".
 */
export async function getAppInfo(): Promise<AppInfo> {
  if (!Platform.isMobile) {
    return WEB_APP_INFO;
  }

  const { App } = await import("@capacitor/app");
  return await App.getInfo();
}

/**
 * Returns the URL the app was cold-started with (e.g. via a custom-scheme deep
 * link or universal link), or `null` if the app was launched normally / on web.
 */
export async function getAppLaunchUrl(): Promise<AppLaunchUrl | null> {
  if (!Platform.isMobile) {
    return null;
  }

  const { App } = await import("@capacitor/app");
  const result = await App.getLaunchUrl();
  return result ?? null;
}

/**
 * Subscribes to `appUrlOpen` events fired when the app is already running and
 * receives a deep link. Web returns a no-op handle.
 */
export async function addAppUrlOpenListener(
  callback: (event: AppUrlOpenEvent) => void
): Promise<AppListenerHandle> {
  if (!Platform.isMobile) {
    return NO_OP_LISTENER_HANDLE;
  }

  const { App } = await import("@capacitor/app");
  return await App.addListener("appUrlOpen", callback);
}

/**
 * Subscribes to `appStateChange` events (foreground/background transitions).
 * Web returns a no-op handle.
 */
export async function addAppStateChangeListener(
  callback: (state: AppStateChange) => void
): Promise<AppListenerHandle> {
  if (!Platform.isMobile) {
    return NO_OP_LISTENER_HANDLE;
  }

  const { App } = await import("@capacitor/app");
  return await App.addListener("appStateChange", callback);
}
